import { useCallback, useRef, useState, useEffect } from 'react'
import {
    DownloadFolder,
    PresignedGetUrl,
    DownloadMultipleFiles,
} from '@/modules/Drive/services'
import { downloadFile } from '@/utils/function'
import { ObjectType, DownloadMultipleFilesParams } from '@/modules/Drive/types'
export interface DownloadParams {
    type: ObjectType
    key: string
    itemName: string
}

export interface UseDownloadFileProps {
    onSuccess?: (params: DownloadParams) => void
    onError?: (params: DownloadParams, error: any) => void
    onCancel?: () => void
}

const REMOVE_DURATION = 5000 // ms

/**
 * Custom hook for managing file and folder downloads with progress and abort support.
 *
 * Recent fixes:
 * - Added AbortController support to folder downloads
 * - Improved downloadMultipleItems progress consistency with folder downloads
 * - Both folder and multiple file downloads now support abort functionality
 * - Progress steps and timing are now consistent between folder and multiple downloads
 */
export const useDownloadFile = (props?: UseDownloadFileProps) => {
    const { onSuccess, onError, onCancel } = props || {}
    const [percent, setPercent] = useState<Record<string, number>>({})
    const [isDownloading, setIsDownloading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    // Refs for managing abort controllers, timeouts, and active downloads
    const abortControllersRef = useRef<Map<string, AbortController>>(new Map())
    const completedTimeoutsRef = useRef<Map<string, NodeJS.Timeout>>(new Map())
    const activeDownloadsRef = useRef<Set<string>>(new Set())

    /**
     * Update download progress for a given key.
     */
    const updateProgress = useCallback(
        (key: string, progressPercent: number) => {
            setPercent((prev) => ({ ...prev, [key]: progressPercent }))
        },
        []
    )

    /**
     * Trigger a file download in the browser.
     */
    const triggerFileDownload = useCallback((blob: Blob, fileName: string) => {
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = fileName
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
    }, [])

    /**
     * Download a folder as a zip file.
     */
    const downloadFolderHandler = useCallback(
        async (key: string, itemName: string, controller?: AbortController) => {
            // Show initial progress
            updateProgress(key, 10)

            // Simulate progress steps for better UX
            const progressSteps = [25, 50, 75]
            let currentStep = 0

            const progressInterval = setInterval(() => {
                if (currentStep < progressSteps.length) {
                    updateProgress(key, progressSteps[currentStep])
                    currentStep++
                }
            }, 1000) // Update every 1s

            try {
                const zipBlob = await DownloadFolder(key, controller?.signal)
                clearInterval(progressInterval)

                const zipName = itemName || 'folder'
                triggerFileDownload(zipBlob, `${zipName}.zip`)
                updateProgress(key, 100)
                return true
            } catch (error: any) {
                clearInterval(progressInterval)
                if (error?.message?.includes('Download cancelled')) {
                    onCancel?.()
                    return false
                }
                throw error
            }
        },
        [triggerFileDownload, updateProgress]
    )

    /**
     * Stream and download a file with progress tracking.
     */
    const streamFileDownload = useCallback(
        async (
            key: string,
            itemName: string,
            presignedUrl: string,
            controller: AbortController
        ) => {
            try {
                const res = await fetch(presignedUrl, {
                    signal: controller.signal,
                })
                if (!res.ok) throw new Error('Network response was not ok')
                const contentLength = res.headers.get('content-length')
                const total = contentLength ? parseInt(contentLength, 10) : 0
                const reader = res.body?.getReader()
                if (!reader) {
                    await downloadFile(presignedUrl, itemName)
                    updateProgress(key, 100)
                    return true
                }
                let received = 0
                const chunks: Uint8Array[] = []
                while (true) {
                    const { done, value } = await reader.read()
                    if (done) break
                    if (value) {
                        chunks.push(value)
                        received += value.length
                        if (total) {
                            const percentValue = Math.floor(
                                (received * 100) / total
                            )
                            updateProgress(key, percentValue)
                        }
                    }
                }
                updateProgress(key, 100)
                const blob = new Blob(chunks as BlobPart[])
                triggerFileDownload(blob, itemName)
                return true
            } catch (err: any) {
                if (
                    err.name === 'AbortError' ||
                    err.code === 'ERR_CANCELED' ||
                    err.name === 'CanceledError'
                ) {
                    throw new Error('Download cancelled')
                }
                throw err
            }
        },
        [triggerFileDownload, updateProgress]
    )

    /**
     * Main download function for files and folders.
     */
    const downloadFileHandler = useCallback(
        async (params: DownloadParams) => {
            if (activeDownloadsRef.current.has(params.key)) {
                // Already downloading
                return
            }
            setIsDownloading(true)
            setError(null)
            updateProgress(params.key, 0)
            activeDownloadsRef.current.add(params.key)
            try {
                let success = false
                if (params.type === ObjectType.FOLDER) {
                    const controller = new AbortController()
                    abortControllersRef.current.set(params.key, controller)
                    success = await downloadFolderHandler(
                        params.key,
                        params.itemName,
                        controller
                    )
                    if (success) {
                        abortControllersRef.current.delete(params.key)
                    }
                } else {
                    const response = await PresignedGetUrl(params.key)
                    const presignedUrl = decodeURIComponent(response.result)
                    const controller = new AbortController()
                    abortControllersRef.current.set(params.key, controller)
                    success = await streamFileDownload(
                        params.key,
                        params.itemName,
                        presignedUrl,
                        controller
                    )
                    if (success) {
                        abortControllersRef.current.delete(params.key)
                    }
                }
                if (!success) {
                    setPercent((prev) => {
                        const updated = { ...prev }
                        delete updated[params.key]
                        return updated
                    })
                } else {
                    // Call onSuccess callback if provided
                    if (onSuccess) {
                        onSuccess(params)
                    }
                }
            } catch (err: any) {
                abortControllersRef.current.delete(params.key)

                if (err?.message?.includes('Download cancelled')) {
                    onCancel?.()
                    return
                }
                setError(err?.message || 'Download failed')
                setPercent((prev) => {
                    const updated = { ...prev }
                    delete updated[params.key]
                    return updated
                })
            } finally {
                activeDownloadsRef.current.delete(params.key)
                if (activeDownloadsRef.current.size === 0) {
                    setIsDownloading(false)
                }
            }
        },
        [updateProgress, downloadFolderHandler, streamFileDownload, onSuccess]
    )

    /**
     * Abort a single download by file name.
     */
    const abortDownload = useCallback((fileName: string) => {
        const controller = abortControllersRef.current.get(fileName)
        if (!controller) return false
        try {
            controller.abort()
            abortControllersRef.current.delete(fileName)
            activeDownloadsRef.current.delete(fileName)
            setPercent((prev) => {
                const updated = { ...prev }
                delete updated[fileName]
                return updated
            })
            return true
        } catch {
            return false
        }
    }, [])

    /**
     * Download multiple files/folders as a single zip.
     */
    const downloadMultipleItems = useCallback(
        async (params: DownloadMultipleFilesParams) => {
            // Create a more descriptive key for the progress bar
            const zipName = params.zipName || `download-${Date.now()}.zip`

            if (activeDownloadsRef.current.has(zipName)) {
                // Already downloading
                return
            }

            setIsDownloading(true)
            setError(null)

            updateProgress(zipName, 10) // Show initial progress like folder download
            activeDownloadsRef.current.add(zipName)

            // Create an abort controller for cancellation
            const controller = new AbortController()
            abortControllersRef.current.set(zipName, controller)
            // Simulate progress for better UX
            const progressSteps = [25, 50, 75]
            let currentStep = 0

            const progressInterval = setInterval(() => {
                if (currentStep < progressSteps.length) {
                    updateProgress(zipName, progressSteps[currentStep])
                    currentStep++
                }
            }, 1000) // Update every 1s to match folder download timing

            try {
                const zipBlob = await DownloadMultipleFiles(
                    params,
                    controller.signal
                )
                triggerFileDownload(zipBlob, zipName)
                clearInterval(progressInterval)
                updateProgress(zipName, 100)

                // Remove the abort controller after successful download
                abortControllersRef.current.delete(zipName)

                // Call onSuccess if provided
                if (onSuccess) {
                    onSuccess({
                        type: ObjectType.FILE,
                        key: zipName,
                        itemName: zipName,
                    })
                }

                // Clean up progress after a delay (let user see 100% for a moment)
                setTimeout(() => {
                    setPercent((prev) => {
                        const updated = { ...prev }
                        delete updated[zipName]
                        return updated
                    })
                }, REMOVE_DURATION)

                return true
            } catch (error: any) {
                clearInterval(progressInterval)
                if (error?.message?.includes('Download cancelled')) {
                    // Download was aborted
                    abortControllersRef.current.delete(zipName)
                    setPercent((prev) => {
                        const updated = { ...prev }
                        delete updated[zipName]
                        return updated
                    })
                    if (onCancel) {
                        onCancel()
                    }
                    return false
                }

                // Clean up abort controller on error
                abortControllersRef.current.delete(zipName)

                const errorMessage = error?.message || 'Download failed'
                setError(errorMessage)

                // Call onError if provided with proper parameters
                if (onError) {
                    onError(
                        {
                            type: ObjectType.FILE,
                            key: zipName,
                            itemName: zipName,
                        },
                        error
                    )
                }

                // Clean up progress after error
                setPercent((prev) => {
                    const updated = { ...prev }
                    delete updated[zipName]
                    return updated
                })

                return false
            } finally {
                activeDownloadsRef.current.delete(zipName)

                // Check if there are any active downloads remaining
                if (activeDownloadsRef.current.size === 0) {
                    setIsDownloading(false)
                }
            }
        },
        [updateProgress, triggerFileDownload, onSuccess, onError, onCancel]
    )

    /**
     * Abort all ongoing downloads.
     */
    const abortAllDownloads = useCallback(() => {
        abortControllersRef.current.forEach((controller) => {
            try {
                controller.abort()
            } catch {
                console.error('Error aborting download')
            }
        })
        abortControllersRef.current.clear()
        activeDownloadsRef.current.clear()
        setPercent({})
    }, [])

    /**
     * Clean up completed downloads after a delay.
     */
    useEffect(() => {
        Object.entries(percent).forEach(([fileName, value]) => {
            if (value === 100 && !completedTimeoutsRef.current.has(fileName)) {
                const timeout = setTimeout(() => {
                    setPercent((prev) => {
                        const updated = { ...prev }
                        delete updated[fileName]
                        return updated
                    })
                    completedTimeoutsRef.current.delete(fileName)
                }, REMOVE_DURATION)
                completedTimeoutsRef.current.set(fileName, timeout)
            }
        })
        completedTimeoutsRef.current.forEach((timeout, fileName) => {
            if (!(fileName in percent)) {
                clearTimeout(timeout)
                completedTimeoutsRef.current.delete(fileName)
            }
        })
    }, [percent])

    /**
     * Clean up on unmount: clear timeouts and abort downloads.
     */
    useEffect(() => {
        return () => {
            completedTimeoutsRef.current.forEach((timeout) =>
                clearTimeout(timeout)
            )
            completedTimeoutsRef.current.clear()
            abortAllDownloads()
        }
    }, [abortAllDownloads])

    return {
        downloadFile: downloadFileHandler,
        downloadMultipleItems,
        isDownloading,
        error,
        percent,
        abortDownload,
        abortAllDownloads,
    }
}
